// src/components/MonCompte.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { User, Lock, Eye, EyeOff, Save, CheckCircle, AlertTriangle,
  Calendar, Clock, Umbrella, Plus, X, ChevronDown, Trash2, Loader2 } from 'lucide-react'
import { apiFetch } from '../api.js'
import { useStore } from '../store/index.js'

const LEAVE_TYPE_LABELS = {
  CONGE_PAYE: 'Congé payé', RTT: 'RTT', MALADIE: 'Maladie',
  SANS_SOLDE: 'Sans solde', AUTRE: 'Autre',
}
const LEAVE_STATUS = {
  PENDING:  { label: 'En attente',  cls: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' },
  APPROVED: { label: 'Approuvé',    cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
  REJECTED: { label: 'Refusé',      cls: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200' },
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
}

// ── Calendrier de sélection de plage ──
const DAYS_FR = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di']
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre']

function countWorkdays(start, end) {
  let count = 0
  const cur = new Date(start)
  while (cur <= end) {
    const d = cur.getDay()
    if (d !== 0 && d !== 6) count++
    cur.setDate(cur.getDate() + 1)
  }
  return count
}

function toYMD(date) {
  return date.toISOString().slice(0, 10)
}

function LeaveCalendar({ startDate, endDate, onSelect }) {
  const today = new Date(); today.setHours(0,0,0,0)
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [hovered, setHovered] = useState(null)

  function prevMonth() {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  function handleDayClick(date) {
    if (date < today) return
    if (!startDate || (startDate && endDate)) {
      onSelect(date, null)
    } else {
      if (date < startDate) { onSelect(date, null) }
      else { onSelect(startDate, date) }
    }
  }

  // Construire la grille du mois
  const firstDay = new Date(viewYear, viewMonth, 1)
  // Lundi = 0
  let startOffset = firstDay.getDay() - 1
  if (startOffset < 0) startOffset = 6
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()

  const cells = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d))

  const rangeEnd = endDate || hovered

  return (
    <div className="select-none">
      {/* Navigation mois */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prevMonth}
          className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-500">
          ‹
        </button>
        <span className="text-sm font-semibold text-slate-700">
          {MONTHS_FR[viewMonth]} {viewYear}
        </span>
        <button type="button" onClick={nextMonth}
          className="rounded-lg p-1.5 hover:bg-slate-100 text-slate-500">
          ›
        </button>
      </div>

      {/* En-têtes jours */}
      <div className="grid grid-cols-7 mb-1">
        {DAYS_FR.map(d => (
          <div key={d} className={`text-center text-xs font-medium pb-1 ${d === 'Sa' || d === 'Di' ? 'text-slate-300' : 'text-slate-400'}`}>
            {d}
          </div>
        ))}
      </div>

      {/* Grille jours */}
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((date, i) => {
          if (!date) return <div key={`e-${i}`} />

          const ymd = toYMD(date)
          const isWeekend = date.getDay() === 0 || date.getDay() === 6
          const isPast = date < today
          const isStart = startDate && toYMD(startDate) === ymd
          const isEnd = endDate && toYMD(endDate) === ymd
          const inRange = startDate && rangeEnd && date > startDate && date < rangeEnd
          const isHoverEnd = !endDate && hovered && toYMD(hovered) === ymd && startDate && date >= startDate
          const isSelected = isStart || isEnd

          let cls = 'relative text-xs h-8 flex items-center justify-center rounded-full transition-colors cursor-pointer '

          if (isPast) {
            cls += 'text-slate-200 cursor-not-allowed '
          } else if (isSelected) {
            cls += 'bg-indigo-600 text-white font-bold z-10 '
          } else if (inRange) {
            cls += 'bg-indigo-100 text-indigo-800 rounded-none '
          } else if (isHoverEnd) {
            cls += 'bg-indigo-50 text-indigo-600 '
          } else if (isWeekend) {
            cls += 'text-slate-300 hover:bg-slate-50 '
          } else {
            cls += 'text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 '
          }

          // Arrondis pour les bords de plage
          if (isStart && (inRange || isHoverEnd)) cls += 'rounded-l-full rounded-r-none '
          if (isEnd && startDate) cls += 'rounded-r-full rounded-l-none '

          return (
            <div key={ymd}
              className={cls}
              onClick={() => !isPast && handleDayClick(date)}
              onMouseEnter={() => !isPast && startDate && !endDate && setHovered(date)}
              onMouseLeave={() => setHovered(null)}>
              {date.getDate()}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function LeaveRequestModal({ onClose, onSaved }) {
  const [type, setType] = useState('CONGE_PAYE')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [reason, setReason] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const workdays = startDate && endDate ? countWorkdays(startDate, endDate) : 0
  const canSubmit = startDate && endDate && workdays > 0

  function handleSelect(start, end) {
    setStartDate(start)
    setEndDate(end)
    setErr(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!canSubmit) return
    setErr(null)
    setSaving(true)
    try {
      await apiFetch('/api/me/leaves', {
        method: 'POST',
        body: {
          type,
          start_date: toYMD(startDate),
          end_date: toYMD(endDate),
          days_count: workdays,
          reason: reason || undefined,
        },
      })
      onSaved()
      onClose()
    } catch (e) {
      setErr(e.message || 'Erreur lors de la demande')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">Nouvelle demande de congé</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
            <select className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={type} onChange={e => setType(e.target.value)}>
              {Object.entries(LEAVE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {/* Calendrier */}
          <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
            <LeaveCalendar
              startDate={startDate}
              endDate={endDate}
              onSelect={handleSelect}
            />
          </div>

          {/* Récap sélection */}
          <div className={`rounded-lg px-3 py-2 text-sm text-center transition-colors ${
            canSubmit ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-slate-50 text-slate-400 border border-slate-200'
          }`}>
            {!startDate
              ? 'Cliquez sur le premier jour'
              : !endDate
              ? 'Cliquez sur le dernier jour'
              : <><strong>{workdays} jour{workdays > 1 ? 's' : ''} ouvré{workdays > 1 ? 's' : ''}</strong>
                  &nbsp;·&nbsp;{fmtDate(startDate)} → {fmtDate(endDate)}</>
            }
          </div>

          {/* Motif */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Motif (optionnel)</label>
            <textarea rows={2}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Ex : vacances, démarches médicales…" />
          </div>

          {err && (
            <div className="flex items-center gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />{err}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
              Annuler
            </button>
            <button type="submit" disabled={saving || !canSubmit}
              className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
              Envoyer la demande
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function PasswordStrength({ password }) {
  if (!password) return null

  const checks = [
    { ok: password.length >= 12, label: '12 caracteres minimum' },
    { ok: /[A-Z]/.test(password), label: 'Une majuscule' },
    { ok: /[a-z]/.test(password), label: 'Une minuscule' },
    { ok: /[0-9]/.test(password), label: 'Un chiffre' },
    { ok: /[^A-Za-z0-9]/.test(password), label: 'Un caractere special' },
  ]

  const score = checks.filter(c => c.ok).length
  const color = score <= 2 ? 'bg-red-500' : score <= 3 ? 'bg-amber-500' : score <= 4 ? 'bg-yellow-400' : 'bg-green-500'
  const label = score <= 2 ? 'Faible' : score <= 3 ? 'Moyen' : score <= 4 ? 'Bon' : 'Fort'

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${(score / 5) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-500">{label}</span>
      </div>
      <ul className="text-xs text-slate-500 space-y-0.5">
        {checks.map(c => (
          <li key={c.label} className={c.ok ? 'text-green-600' : ''}>
            {c.ok ? '\u2713' : '\u2022'} {c.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function MonCompte() {
  const user = useStore(s => s.user)
  const setAuth = useStore(s => s.setAuth)

  const initials = useMemo(() => {
    const name = user?.full_name || user?.username || ''
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return 'U'
    return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase()
  }, [user])

  // Profile state
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [profileMsg, setProfileMsg] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)

  // Password state
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [pwMsg, setPwMsg] = useState(null)
  const [pwLoading, setPwLoading] = useState(false)

  // Congés state
  const [employee, setEmployee] = useState(null)
  const [balance, setBalance] = useState(null)
  const [leaves, setLeaves] = useState([])
  const [leavesLoading, setLeavesLoading] = useState(true)
  const [showLeaveModal, setShowLeaveModal] = useState(false)
  const [cancelingId, setCancelingId] = useState(null)

  const loadLeaves = useCallback(async () => {
    setLeavesLoading(true)
    try {
      const [emp, bal, lvs] = await Promise.all([
        apiFetch('/api/me/employee').catch(() => null),
        apiFetch('/api/me/leave-balance').catch(() => null),
        apiFetch('/api/me/leaves').catch(() => []),
      ])
      setEmployee(emp)
      setBalance(bal)
      setLeaves(Array.isArray(lvs) ? lvs : [])
    } finally {
      setLeavesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user) {
      setEmail(user.email || '')
      setFullName(user.full_name || '')
    }
  }, [user])

  useEffect(() => { loadLeaves() }, [loadLeaves])

  async function handleCancelLeave(id) {
    if (!confirm('Annuler cette demande de congé ?')) return
    setCancelingId(id)
    try {
      await apiFetch(`/api/me/leaves/${id}`, { method: 'DELETE' })
      await loadLeaves()
    } catch (e) {
      alert(e.message || 'Impossible d\'annuler')
    } finally {
      setCancelingId(null)
    }
  }

  async function handleProfileSave(e) {
    e.preventDefault()
    setProfileMsg(null)
    setProfileLoading(true)

    try {
      const data = await apiFetch('/api/auth/profile', {
        method: 'PATCH',
        body: { email: email || null, full_name: fullName || null },
      })

      // Update local store
      if (data && user) {
        setAuth({ ...user, email: data.email, full_name: data.full_name }, null)
      }

      setProfileMsg({ type: 'success', text: 'Profil mis a jour.' })
    } catch (err) {
      const msg = err?.data?.error || err?.message || 'Erreur'
      if (msg === 'email_already_used') {
        setProfileMsg({ type: 'error', text: 'Cet email est deja utilise par un autre compte.' })
      } else if (msg === 'invalid_email') {
        setProfileMsg({ type: 'error', text: 'Format email invalide.' })
      } else {
        setProfileMsg({ type: 'error', text: msg })
      }
    } finally {
      setProfileLoading(false)
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault()
    setPwMsg(null)

    if (newPw !== confirmPw) {
      setPwMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas.' })
      return
    }

    setPwLoading(true)

    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { current_password: currentPw, new_password: newPw },
      })

      setCurrentPw('')
      setNewPw('')
      setConfirmPw('')
      setPwMsg({ type: 'success', text: 'Mot de passe modifie avec succes.' })
    } catch (err) {
      const code = err?.data?.error || err?.message || 'Erreur'
      if (code === 'bad_current_password') {
        setPwMsg({ type: 'error', text: 'Le mot de passe actuel est incorrect.' })
      } else if (code === 'password_too_weak') {
        setPwMsg({ type: 'error', text: err?.data?.message || 'Le mot de passe est trop faible.' })
      } else {
        setPwMsg({ type: 'error', text: code })
      }
    } finally {
      setPwLoading(false)
    }
  }

  const inputCls = 'pilot-input'

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="pilot-card p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-14 w-14 rounded-2xl bg-slate-900 text-white flex items-center justify-center text-lg font-black">
              {initials}
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-slate-900">Mon compte</h1>
              <p className="text-sm text-slate-500 mt-1">
                Gere tes informations personnelles et ta securite.
              </p>
              <div className="text-xs text-slate-500 mt-1 truncate">
                {user?.email || 'email non renseigne'}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <span className={`h-2 w-2 rounded-full ${user ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {user ? 'Connecte' : 'Hors ligne'}
          </div>
        </div>
      </div>

      {/* Section Profil */}
      <form onSubmit={handleProfileSave} className="pilot-card">
        <div className="pilot-card-header">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center">
              <User size={18} className="text-slate-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Informations personnelles</h2>
              <p className="text-xs text-slate-500">Modifie ton email et ton nom complet.</p>
            </div>
          </div>
          <div className="pilot-divider" />
        </div>
        <div className="pilot-card-body space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Nom d'utilisateur</label>
            <input
              className={`${inputCls} bg-slate-50 cursor-not-allowed`}
              value={user?.username || ''}
              disabled
            />
            <p className="text-xs text-slate-500 mt-1">Le nom d'utilisateur ne peut pas etre modifie.</p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <input
              className={inputCls}
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setProfileMsg(null) }}
              placeholder="votre@email.com"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Nom complet</label>
            <input
              className={inputCls}
              value={fullName}
              onChange={e => { setFullName(e.target.value); setProfileMsg(null) }}
              placeholder="Prenom Nom"
            />
          </div>

          {profileMsg && (
            <div className={`flex items-center gap-2 rounded-2xl p-3 text-sm ${
              profileMsg.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {profileMsg.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
              {profileMsg.text}
            </div>
          )}

          <button
            type="submit"
            disabled={profileLoading}
            className="pilot-btn pilot-btn-primary disabled:opacity-50"
          >
            <Save size={16} />
            {profileLoading ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Section Securite */}
      <form onSubmit={handlePasswordChange} className="pilot-card">
        <div className="pilot-card-header">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-amber-100 flex items-center justify-center">
              <Lock size={18} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Securite</h2>
              <p className="text-xs text-slate-500">Change ton mot de passe.</p>
            </div>
          </div>
          <div className="pilot-divider" />
        </div>
        <div className="pilot-card-body space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700">Mot de passe actuel</label>
            <div className="relative mt-1">
              <input
                className={inputCls}
                type={showCurrent ? 'text' : 'password'}
                value={currentPw}
                onChange={e => { setCurrentPw(e.target.value); setPwMsg(null) }}
                placeholder="Mot de passe actuel"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Nouveau mot de passe</label>
            <div className="relative mt-1">
              <input
                className={inputCls}
                type={showNew ? 'text' : 'password'}
                value={newPw}
                onChange={e => { setNewPw(e.target.value); setPwMsg(null) }}
                placeholder="Nouveau mot de passe"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <PasswordStrength password={newPw} />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Confirmer le nouveau mot de passe</label>
            <input
              className={inputCls}
              type="password"
              value={confirmPw}
              onChange={e => { setConfirmPw(e.target.value); setPwMsg(null) }}
              placeholder="Confirmer le mot de passe"
              autoComplete="new-password"
            />
            {confirmPw && newPw !== confirmPw && (
              <p className="text-xs text-red-500 mt-1">Les mots de passe ne correspondent pas.</p>
            )}
          </div>

          {pwMsg && (
            <div className={`flex items-center gap-2 rounded-2xl p-3 text-sm ${
              pwMsg.type === 'success'
                ? 'bg-green-50 text-green-700 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {pwMsg.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
              {pwMsg.text}
            </div>
          )}

          <button
            type="submit"
            disabled={pwLoading || !currentPw || !newPw || !confirmPw || newPw !== confirmPw}
            className="pilot-btn bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Lock size={16} />
            {pwLoading ? 'Modification...' : 'Changer le mot de passe'}
          </button>
        </div>
      </form>

      {/* ── Section Congés ── */}
      <div className="pilot-card">
        <div className="pilot-card-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-2xl bg-indigo-100 flex items-center justify-center">
                <Umbrella size={18} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Mes congés</h2>
                <p className="text-xs text-slate-500">Pose et suis tes demandes de congé.</p>
              </div>
            </div>
            {employee && (
              <button
                onClick={() => setShowLeaveModal(true)}
                className="flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                <Plus size={15} /> Nouvelle demande
              </button>
            )}
          </div>
          <div className="pilot-divider" />
        </div>

        <div className="pilot-card-body">
          {leavesLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement…
            </div>
          ) : !employee ? (
            <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
              Aucune fiche employé n'est liée à votre compte.<br />
              <span className="text-xs text-slate-400 mt-1 block">Demandez à votre responsable de faire le lien dans le module RH.</span>
            </div>
          ) : (
            <>
              {/* Solde */}
              {balance && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { label: 'Acquis / an', value: balance.annual, color: 'text-slate-700', bg: 'bg-slate-50' },
                    { label: 'Pris', value: balance.used, color: 'text-rose-600', bg: 'bg-rose-50' },
                    { label: 'Restants', value: balance.remaining, color: 'text-emerald-700', bg: 'bg-emerald-50' },
                  ].map(({ label, value, color, bg }) => (
                    <div key={label} className={`rounded-xl ${bg} p-4 text-center`}>
                      <div className={`text-2xl font-bold ${color}`}>{value}</div>
                      <div className="text-xs text-slate-500 mt-1">{label}</div>
                    </div>
                  ))}
                </div>
              )}
              {balance?.pending > 0 && (
                <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  {balance.pending} jour(s) en attente d'approbation
                </div>
              )}

              {/* Historique */}
              {leaves.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">Aucune demande de congé pour l'instant.</p>
              ) : (
                <div className="space-y-2">
                  {leaves.map((lr) => {
                    const st = LEAVE_STATUS[lr.status] || { label: lr.status, cls: 'bg-slate-100 text-slate-600' }
                    return (
                      <div key={lr.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 hover:bg-slate-50 transition-colors">
                        <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-800">
                              {LEAVE_TYPE_LABELS[lr.type] || lr.type}
                            </span>
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${st.cls}`}>
                              {st.label}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {fmtDate(lr.start_date)} → {fmtDate(lr.end_date)}
                            &nbsp;·&nbsp;<strong>{lr.days_count} j</strong>
                            {lr.reason && <span className="ml-1 text-slate-400">· {lr.reason}</span>}
                          </div>
                          {lr.status === 'REJECTED' && lr.rejection_reason && (
                            <div className="text-xs text-rose-500 mt-0.5">Motif refus : {lr.rejection_reason}</div>
                          )}
                          {lr.status === 'APPROVED' && lr.approved_by_username && (
                            <div className="text-xs text-emerald-600 mt-0.5">Approuvé par @{lr.approved_by_username}</div>
                          )}
                        </div>
                        {lr.status === 'PENDING' && (
                          <button
                            onClick={() => handleCancelLeave(lr.id)}
                            disabled={cancelingId === lr.id}
                            title="Annuler la demande"
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600 transition-colors disabled:opacity-40">
                            {cancelingId === lr.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4" />}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showLeaveModal && (
        <LeaveRequestModal
          onClose={() => setShowLeaveModal(false)}
          onSaved={loadLeaves}
        />
      )}
    </div>
  )
}
